import { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { Activity } from 'lucide-react';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

const LOGIN_PORTAL_URL =
  process.env.REACT_APP_LOGIN_PORTAL_URL || 'https://login.someaditya.in/';

// Strip the SSO fragment from the URL synchronously, so a refresh of the
// destination app doesn't try to re-sign-in with a now-spent token.
// Returns the extracted token (or null).
function consumeSsoToken() {
  const hash = window.location.hash || '';
  const match = hash.match(/(?:^|#|&)sso_token=([^&]+)/i);
  if (!match) return null;
  const token = decodeURIComponent(match[1]);
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
  return token;
}

function redirectToLoginPortal() {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.replace(`${LOGIN_PORTAL_URL}?return_to=${returnTo}`);
}

const screenBg = {
  background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1f35 100%)',
};

export default function ProtectedRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  // If we consumed an SSO token but it failed to sign us in, surface
  // the error and STOP the redirect-loop. Without this guard the page
  // would just bounce back to the portal, which would mint a fresh
  // token, redirect us back here, fail again, forever.
  const [ssoError, setSsoError] = useState(null);

  // Enable idle timeout (30 minutes of inactivity)
  useIdleTimeout(30);

  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    async function bootstrap() {
      const ssoToken = consumeSsoToken();
      let ssoAttempted = false;
      if (ssoToken) {
        ssoAttempted = true;
        try {
          await signInWithCustomToken(auth, ssoToken);
        } catch (err) {
          console.error('SSO sign-in failed', err);
          if (!cancelled) {
            setSsoError({
              code: err.code || 'unknown',
              message: err.message || String(err),
            });
            setLoading(false);
            return;
          }
        }
      }
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(auth, (user) => {
        // If we just consumed an SSO token but Firebase still sees no
        // user, treat that as an SSO failure too — otherwise we'd loop.
        if (ssoAttempted && !user) {
          setSsoError({
            code: 'no-user-after-sso',
            message:
              'Custom token was accepted but no user appeared in Firebase Auth state.',
          });
          setLoading(false);
          return;
        }
        setAuthenticated(!!user);
        setLoading(false);
      });
    }

    bootstrap();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={screenBg}
      >
        <Activity className="animate-spin" size={48} style={{ color: '#60a5fa' }} />
      </div>
    );
  }

  if (ssoError) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 text-center"
        style={screenBg}
      >
        <div
          className="max-w-md w-full p-8 rounded-2xl"
          style={{
            background: 'rgba(20, 25, 45, 0.8)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#f5f5f7',
          }}
        >
          <h1 className="text-2xl font-bold mb-3" style={{ color: '#fca5a5' }}>
            Sign-in failed
          </h1>
          <p className="text-sm mb-4 text-gray-300">
            The login portal issued a token but this app couldn't use it.
          </p>
          <pre
            className="text-xs text-left p-3 rounded mb-5 overflow-x-auto"
            style={{
              background: 'rgba(0,0,0,0.4)',
              color: '#fca5a5',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {ssoError.code}: {ssoError.message}
          </pre>
          <button
            onClick={() => {
              // Force a full logout via the portal so we don't loop on
              // a stale Firebase session when we retry.
              window.location.replace(`${LOGIN_PORTAL_URL}?action=logout`);
            }}
            className="px-4 py-2 rounded font-semibold"
            style={{
              background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Sign out and try again
          </button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    redirectToLoginPortal();
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={screenBg}
      >
        <Activity className="animate-spin" size={48} style={{ color: '#60a5fa' }} />
      </div>
    );
  }

  return children;
}
